import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { HttpParams } from '@angular/common/http';
import { of, Subject, Subscription } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { API_BASE_URL_SERVICE } from '../../../../../projects/kiene-core/src/lib/kiene-core.config';
import { MessageService } from '../../../../../projects/kiene-core/src/lib/services/message-service/message.service';
import { KieneBackendApiService } from '../../../../../projects/kiene-core/src/lib/services/backend-api/kiene-backend-api.service';
import { LocalStorageService } from '../../../../../projects/kiene-core/src/lib/services/local-storage/local-storage.service';
import { Supplier } from '../../../warehouse/supplier/supplier';

export interface NebenkostenLieferantPickerRow {
    lieferant_id: number;
    bezeichnung?: string;
    bezeichnung_kurz?: string;
    buchungskonto?: string;
    buchungsgegenkonto?: string;
    nebenkosten?: number;
}

@Component({
    selector: 'kiene-nebenkosten-lieferant-picker-dialog',
    templateUrl: './nebenkosten-lieferant-picker-dialog.component.html',
    styleUrls: ['./nebenkosten-lieferant-picker-dialog.component.scss'],
})
export class NebenkostenLieferantPickerDialogComponent implements OnInit, OnDestroy {
    searchCtrl = new UntypedFormControl('');
    results: NebenkostenLieferantPickerRow[] = [];
    loading = false;
    showCreate = false;
    creating = false;
    /** Anlegen mit Lieferanten-Recht 1072. */
    canCreate = false;

    bezeichnungCtrl = new UntypedFormControl('', [Validators.required]);
    buchungskontoCtrl = new UntypedFormControl('', [Validators.required]);
    buchungsgegenkontoCtrl = new UntypedFormControl('4530');

    private searchSub?: Subscription;
    private readonly search$ = new Subject<string>();

    constructor(
        public dialogRef: MatDialogRef<NebenkostenLieferantPickerDialogComponent, Supplier | null>,
        @Inject(MAT_DIALOG_DATA) public data: { initialSearch?: string; honorar?: boolean },
        @Inject(API_BASE_URL_SERVICE) private readonly apiBaseUrl: string,
        private readonly api: KieneBackendApiService,
        private readonly messageService: MessageService,
        private readonly localStorage: LocalStorageService,
    ) {}

    ngOnInit(): void {
        this.canCreate =
            !this.data?.honorar && this.localStorage.currentUserHasPermission(1072);
        this.searchSub = this.search$
            .pipe(
                debounceTime(250),
                distinctUntilChanged(),
                switchMap(q => {
                    const term = (q || '').trim();
                    if (term.length < 1) {
                        this.results = [];
                        this.loading = false;
                        return of(null);
                    }
                    this.loading = true;
                    let params = new HttpParams()
                        .set('search', term)
                        .set('limit', '40')
                        .set('offset', '0');
                    if (!this.data?.honorar) {
                        params = params.set('nebenkosten', '1');
                    }
                    return this.api.get(this.apiBaseUrl + 'lieferant/read.php', params).pipe(
                        catchError(err => {
                            this.loading = false;
                            this.results = [];
                            if (err?.status !== 0) {
                                this.messageService.errorMessage('Suche fehlgeschlagen.', err);
                            }
                            return of(null);
                        }),
                    );
                }),
            )
            .subscribe({
                next: res => {
                    this.loading = false;
                    if (!res) {
                        return;
                    }
                    const records = Array.isArray(res?.records) ? res.records : Array.isArray(res) ? res : [];
                    this.results = records as NebenkostenLieferantPickerRow[];
                },
            });

        this.searchCtrl.valueChanges.subscribe((v: string) => this.search$.next(v || ''));

        const initial = (this.data?.initialSearch || '').trim();
        if (initial) {
            this.searchCtrl.setValue(initial);
        }
    }

    ngOnDestroy(): void {
        this.searchSub?.unsubscribe();
    }

    select(row: NebenkostenLieferantPickerRow): void {
        this.dialogRef.close(this.toSupplier(row));
    }

    toggleCreate(): void {
        if (!this.canCreate) {
            this.messageService.infoMessage('Kein Recht zum Anlegen von Lieferanten (1072).');
            return;
        }
        this.showCreate = !this.showCreate;
        if (this.showCreate && !this.bezeichnungCtrl.value) {
            const q = (this.searchCtrl.value || '').trim();
            if (q && !/^\d+(\.\d+)?$/.test(q)) {
                this.bezeichnungCtrl.setValue(q);
            } else if (q && /^\d+(\.\d+)?$/.test(q)) {
                this.buchungskontoCtrl.setValue(q);
            }
        }
    }

    isCreateValid(): boolean {
        return this.bezeichnungCtrl.valid && this.buchungskontoCtrl.valid;
    }

    create(): void {
        if (!this.canCreate || !this.isCreateValid() || this.creating) {
            return;
        }
        this.creating = true;
        const bezeichnung = (this.bezeichnungCtrl.value || '').trim();
        const buchungskonto = (this.buchungskontoCtrl.value || '').trim();
        const buchungsgegenkonto = (this.buchungsgegenkontoCtrl.value || '').trim() || null;
        const allgemeinBody = {
            bezeichnung,
            bezeichnung_kurz: bezeichnung.substring(0, 40),
            nebenkosten: 1,
            fax_bevorzugen: 0,
        };
        // Allgemein legt an (ohne Konten); Konten wie Stammdaten über update_fibu.
        this.api.post(this.apiBaseUrl + 'lieferant/update_allgemein.php', allgemeinBody).subscribe({
            next: (res: NebenkostenLieferantPickerRow | { records?: NebenkostenLieferantPickerRow[] }) => {
                const created =
                    res && (res as NebenkostenLieferantPickerRow).lieferant_id
                        ? (res as NebenkostenLieferantPickerRow)
                        : Array.isArray((res as any)?.records)
                          ? (res as any).records[0]
                          : null;
                if (!created?.lieferant_id) {
                    this.creating = false;
                    this.messageService.infoMessage('Anlegen ohne gültige Antwort.');
                    return;
                }
                const fibuBody = {
                    lieferant_id: created.lieferant_id,
                    buchungskonto,
                    buchungsgegenkonto,
                };
                this.api.post(this.apiBaseUrl + 'lieferant/update_fibu.php', fibuBody).subscribe({
                    next: (fibuRes: NebenkostenLieferantPickerRow) => {
                        this.creating = false;
                        const row: NebenkostenLieferantPickerRow = {
                            ...created,
                            ...(fibuRes || {}),
                            lieferant_id: created.lieferant_id,
                            bezeichnung: created.bezeichnung || bezeichnung,
                            buchungskonto: fibuRes?.buchungskonto || buchungskonto,
                            buchungsgegenkonto: fibuRes?.buchungsgegenkonto || buchungsgegenkonto,
                            nebenkosten: 1,
                        };
                        this.messageService.infoMessage('Nebenkosten-Lieferant angelegt.');
                        this.dialogRef.close(this.toSupplier(row));
                    },
                    error: err => {
                        this.creating = false;
                        this.messageService.errorMessage(
                            'Lieferant angelegt, FiBu-Konten nicht gespeichert.',
                            err,
                        );
                        this.dialogRef.close(
                            this.toSupplier({
                                ...created,
                                bezeichnung: created.bezeichnung || bezeichnung,
                                buchungskonto,
                                buchungsgegenkonto: buchungsgegenkonto || undefined,
                                nebenkosten: 1,
                            }),
                        );
                    },
                });
            },
            error: err => {
                this.creating = false;
                this.messageService.errorMessage('Anlegen fehlgeschlagen.', err);
            },
        });
    }

    close(): void {
        this.dialogRef.close(null);
    }

    private toSupplier(row: NebenkostenLieferantPickerRow): Supplier {
        const s = new Supplier();
        s.lieferant_id = row?.lieferant_id;
        s.bezeichnung = row?.bezeichnung || '';
        s.bezeichnung_kurz = row?.bezeichnung_kurz || '';
        s.buchungskonto = row?.buchungskonto || '';
        s.buchungsgegenkonto = row?.buchungsgegenkonto || '';
        s.nebenkosten = 1;
        return s;
    }
}
